import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { STORAGE_BUCKET } from '@/lib/file-upload-constants';

export interface UploadProgress {
  completed: number;
  total: number;
  percent: number;
  currentFile: string;
}

interface UseFileUploadReturn {
  uploadFiles: (files: File[], prefix: string) => Promise<string[]>;
  isUploading: boolean;
  uploadError: string | null;
  progress: UploadProgress | null;
}

/**
 * Sanitise filename: remove special chars, keep extension.
 */
function sanitizeFilename(name: string): string {
  const parts = name.split('.');
  const ext = parts.length > 1 ? '.' + parts.pop()!.toLowerCase() : '';
  const base = parts
    .join('.')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .substring(0, 100);
  return base + ext;
}

export function useFileUpload(): UseFileUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const uploadFiles = useCallback(async (files: File[], prefix: string): Promise<string[]> => {
    if (files.length === 0) return [];

    setIsUploading(true);
    setUploadError(null);
    setProgress({ completed: 0, total: files.length, percent: 0, currentFile: files[0].name });

    const paths: string[] = [];
    const timestamp = Date.now();

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgress({ completed: i, total: files.length, percent: Math.round((i / files.length) * 100), currentFile: file.name });
        const safeName = sanitizeFilename(file.name);
        const path = `${prefix}/${timestamp}_${safeName}`;

        const { error } = await supabase.storage
          .from(STORAGE_BUCKET)
          .upload(path, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (error) {
          // Rollback: delete already uploaded files
          if (paths.length > 0) {
            await supabase.storage.from(STORAGE_BUCKET).remove(paths);
          }
          throw new Error(`Upload fehlgeschlagen: ${error.message}`);
        }

        paths.push(path);
        setProgress({ completed: i + 1, total: files.length, percent: Math.round(((i + 1) / files.length) * 90), currentFile: file.name });
      }

      setProgress({ completed: files.length, total: files.length, percent: 95, currentFile: 'Validierung...' });
      // Server-side validation via edge function
      const { data: validationResult, error: fnError } = await supabase.functions.invoke(
        'validate-upload',
        { body: { paths } }
      );

      if (fnError || !validationResult?.valid) {
        // Cleanup invalid files
        await supabase.storage.from(STORAGE_BUCKET).remove(paths);
        throw new Error(validationResult?.error ?? 'Serverseitige Validierung fehlgeschlagen');
      }

      setProgress({ completed: files.length, total: files.length, percent: 100, currentFile: '' });
      return paths;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload fehlgeschlagen';
      setUploadError(message);
      throw err;
    } finally {
      setIsUploading(false);
      setTimeout(() => setProgress(null), 500);
    }
  }, []);

  return { uploadFiles, isUploading, uploadError, progress };
}
